import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ChevronRight, Loader2, AlertCircle, ChevronDown, Shield, PenLine } from "lucide-react";
import { toast } from "sonner";
import { STAGE_PERSONAS } from "@/lib/persona-avatars";

const STAGE_INFO = {
  ankommen: { icon: "1", subtitle: "Kennenlernen und Orientierung", detail: "Wir lernen uns kennen. Sie erzählen frei und ich höre zu. Kein Druck, keine Bewertung.", transition: null },
  verstehen: { icon: "2", subtitle: "Schwächen erkennen und verstehen", detail: "Ich zeige Ihnen, worauf Gutachter achten und warum bestimmte Antwortmuster auffallen.", transition: "Ab jetzt schauen wir gemeinsam auf Ihre Antworten. Ich erkläre Ihnen, was Gutachter bemerken würden." },
  aufarbeiten: { icon: "3", subtitle: "Aktiv an Verbesserungen arbeiten", detail: "Wir arbeiten gemeinsam an konkreten Formulierungen. Sie üben, ich gebe Feedback.", transition: "Jetzt wird es aktiv: Sie formulieren Ihre Antworten neu, und ich helfe Ihnen dabei." },
  trainieren: { icon: "4", subtitle: "Gesprächskompetenz aufbauen", detail: "Wir üben ein flüssiges Gespräch mit gezielten Nachfragen. Mehr Tempo, weniger Erklärung.", transition: "Ab jetzt üben wir wie in einem echten Gespräch. Schneller und direkter als bisher." },
  vertiefen: { icon: "5", subtitle: "Belastbarkeit und Konsistenz testen", detail: "Kritischere Fragen, verschiedene Gesprächsstile. Ich prüfe, ob Ihre Antworten stabil bleiben.", transition: "Jetzt wird es anspruchsvoller: Kritischere Fragen, unerwartete Nachfragen." },
  simulation: { icon: "S", subtitle: "Realistische MPU-Simulation", detail: "Ein vollständiges Übungsgespräch wie in der echten MPU. Danach: strukturierte Nachbesprechung.", transition: "Dies ist eine vollständige Prüfungssimulation." }
};

export default function LearningPathPage() {
  const navigate = useNavigate();
  const [path, setPath] = useState(null);
  const [activeSessions, setActiveSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/learning/path"),
      api.get("/training/sessions").catch(() => ({ data: [] }))
    ]).then(([pathRes, sessRes]) => {
      setPath(pathRes.data);
      // Find the most recent active session per stage
      const active = {};
      for (const s of (sessRes.data || [])) {
        if (s.status === "active" && s.learning_stage) {
          if (!active[s.learning_stage] || s.created_at > active[s.learning_stage].created_at) {
            active[s.learning_stage] = s;
          }
        }
      }
      setActiveSessions(active);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startOrResume = async (stage) => {
    // If there's an active session for this stage, resume it
    if (activeSessions[stage]) {
      navigate(`/conversation/${activeSessions[stage].session_id}`);
      return;
    }
    // Otherwise create new
    setStarting(stage);
    try {
      const res = await api.post(`/learning/session?stage=${stage}`);
      if (res.data?.session_id) {
        navigate(`/conversation/${res.data.session_id}`);
        return;
      }
      toast.error("Session konnte nicht erstellt werden");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Session konnte nicht gestartet werden. Bitte versuchen Sie es erneut.");
    }
    setStarting(null);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!path) return null;

  return (
    <div className="page-transition max-w-2xl mx-auto space-y-5" data-testid="learning-path-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Mein Lernpfad</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Schritt für Schritt zur Vorbereitung</p>
      </div>

      {/* Development State */}
      <Card className="border-border/50">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start gap-3">
            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${path.development_state === "simulation_sinnvoll" ? "bg-success" : path.development_state === "fortgeschritten" ? "bg-primary" : "bg-warning"}`} />
            <div>
              <p className="font-body text-sm font-medium">{path.development_label}</p>
              {path.progress_note && <p className="font-body text-xs text-muted-foreground mt-0.5">{path.progress_note}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Focus */}
      {path.recommendation && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-4 pb-3">
            <p className="font-body text-xs font-medium text-primary mb-1">Ihr Fokus gerade</p>
            <p className="font-body text-sm leading-relaxed">{path.recommendation}</p>
          </CardContent>
        </Card>
      )}

      {/* Stages */}
      <div className="space-y-2.5">
        {(path.stages_order || []).map((key, idx) => {
          const stage = path.stages?.[key];
          const info = STAGE_INFO[key];
          if (!stage || !info) return null;
          const isCompleted = stage.status === "completed";
          const isRecommended = stage.status === "recommended";
          const isSimulation = key === "simulation";

          return (
            <div key={key}>
              {isSimulation && idx > 0 && <p className="font-body text-[10px] uppercase tracking-widest text-muted-foreground mt-4 mb-1 px-1">Prüfungsbereich</p>}
              <Card className={`border-border/50 transition-all ${isRecommended ? "border-primary/30 shadow-sm" : isSimulation ? "bg-muted/20" : !isCompleted ? "opacity-60" : ""}`} data-testid={`stage-${key}`}>
                <CardContent className="pt-3.5 pb-3">
                  <div className="flex items-center gap-3.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold ${isCompleted ? "bg-success text-white" : isRecommended ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>
                      {isCompleted ? <Check className="w-4 h-4" /> : info.icon}
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedStage(expandedStage === key ? null : key)}>
                      <div className="flex items-center gap-2">
                        <p className="font-body text-sm font-medium">{stage.name}</p>
                        {isRecommended && <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5 py-0">Empfohlen</Badge>}
                        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expandedStage === key ? "rotate-180" : ""}`} />
                      </div>
                      <p className="font-body text-xs text-muted-foreground">{info.subtitle}</p>
                    </div>
                    <Button size="sm" variant={isRecommended ? "default" : "outline"}
                      className={`flex-shrink-0 font-body text-xs gap-1 ${isRecommended ? "bg-primary text-primary-foreground" : ""}`}
                      onClick={() => startOrResume(key)} disabled={starting === key} data-testid={`start-stage-${key}`}>
                      {starting === key ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      {activeSessions[key] ? "Fortsetzen" : isCompleted ? "Nochmal" : isRecommended ? "Starten" : "Öffnen"} <ChevronRight className="w-3 h-3" />
                    </Button>
                  </div>
                  {expandedStage === key && (
                    <div className="mt-3 pl-[3.25rem] space-y-1.5">
                      {STAGE_PERSONAS[key] && (
                        <div className="flex items-center gap-2 mb-1">
                          <img src={STAGE_PERSONAS[key].image} alt={STAGE_PERSONAS[key].name} className="w-7 h-7 rounded-full object-cover" />
                          <div>
                            <p className="font-body text-xs font-medium">{STAGE_PERSONAS[key].name}</p>
                            <p className="font-body text-[10px] text-muted-foreground">{STAGE_PERSONAS[key].role}</p>
                          </div>
                        </div>
                      )}
                      {info.detail && <p className="font-body text-xs text-muted-foreground">{info.detail}</p>}
                      {info.transition && <p className="font-body text-xs text-primary bg-primary/5 rounded px-2 py-1">{info.transition}</p>}
                      {isSimulation && path.development_state === "im_aufbau" && <p className="font-body text-xs text-muted-foreground flex items-start gap-1"><AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />Nutzen Sie das als Standortbestimmung.</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Fragetechnik */}
      <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-transparent" data-testid="fragetechnik-card">
        <CardContent className="pt-6 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-body text-sm font-medium">Fragetechnik-Training</p>
                <p className="font-body text-xs text-muted-foreground truncate">
                  {path.main_focus ? `Fokus: ${
                    {ausweichverhalten:"Ausweichverhalten",bagatellisierung:"Bagatellisierung",schuldzuweisung:"Schuldzuweisung",fehlende_details:"Fehlende Details",widersprueche:"Widersprüche",einstudiert:"Einstudiert wirkend",rueckfallpraevention:"Rückfallprävention"}[path.main_focus] || path.main_focus
                  }` : "Lernen Sie, Gutachter-Fragen präzise zu beantworten"}
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" className="font-body text-xs gap-1 flex-shrink-0" onClick={() => navigate("/fragetechnik")} data-testid="start-fragetechnik">
              Üben <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Weitere Übungsmodi */}
      <div className="grid grid-cols-2 gap-3" data-testid="additional-modes">
        <Card className="border-border/50 cursor-pointer hover:border-primary/20 hover:shadow-sm transition-all" onClick={() => navigate("/exam")} data-testid="exam-mode-link">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-body text-xs font-medium">Prüfungsmodus</p>
                <p className="font-body text-[10px] text-muted-foreground">Realistische Simulation</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 cursor-pointer hover:border-primary/20 hover:shadow-sm transition-all" onClick={() => navigate("/story")} data-testid="story-mode-link">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <PenLine className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-body text-xs font-medium">Reflexion</p>
                <p className="font-body text-[10px] text-muted-foreground">Ihre Geschichte aufarbeiten</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="font-body text-xs text-muted-foreground text-center">Übungswerkzeug — ersetzt keine professionelle MPU-Beratung.</p>
    </div>
  );
}
